import React, { useState, useCallback, useEffect } from "react";
import { useEditor, EditorContent, EditorContext } from "@tiptap/react";
import { Button } from "@/components/ui/button";
import {
  Edit,
  Save,
  X,
  Check,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Highlighter,
  Link as LinkIcon,
  Heading1,
  Heading2,
  Heading3,
  Maximize2,
  Minimize2,
} from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { AIContentRenderer } from "./AIContentRenderer";
import { CopyIconButton } from "./CopyContentButton";

// Import Tiptap UI components for advanced functionality
import { useLinkPopover } from "@/components/tiptap-ui/link-popover";
import { useHeading } from "@/components/tiptap-ui/heading-button";

// Import essential Tiptap extensions for rich text editing
import { StarterKit } from "@tiptap/starter-kit";
import { Image } from "@tiptap/extension-image";
import { TaskList, TaskItem } from "@tiptap/extension-list";
import { Color, TextStyle } from "@tiptap/extension-text-style";
import { Placeholder } from "@tiptap/extensions";
import { Typography } from "@tiptap/extension-typography";
import { Highlight } from "@tiptap/extension-highlight";
import { Superscript } from "@tiptap/extension-superscript";
import { Subscript } from "@tiptap/extension-subscript";
import { TextAlign } from "@tiptap/extension-text-align";
import { Underline } from "@tiptap/extension-underline";
import { Link } from "@tiptap/extension-link";
import { Editor, JSONContent } from "@tiptap/core";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { useAutoSave } from "@/hooks/useAutoSave";

// TipTap Pro conversion extensions
import { Import } from "@tiptap-pro/extension-import";
import { Export } from "@tiptap-pro/extension-export";

interface EditableAIContentRendererProps {
  content: string | Record<string, any>;
  className?: string;
  onSave?: (newContent: string | Record<string, any>) => Promise<void>;
  onEnterEdit?: () => void;
  canEdit?: boolean;
  schema?: any;
  brandColor?: string;
  // If true, always render editor view (Notion-style)
  alwaysEdit?: boolean;
  // Persist editing lock per output
  persistKey?: string;
  // Optional: TipTap JSON initial document
  initialJson?: JSONContent;
}

interface EditableSection {
  id: string;
  originalContent: string;
  isEditing: boolean;
}

/**
 * Convert structured content to markdown for TipTap native import
 */
const convertContentToMarkdown = (content: any): string => {
  if (typeof content === "string") {
    // For display, we keep the content as-is since AIContentRenderer will handle it
    // Only clean when we're actually editing
    return content;
  }

  if (typeof content === "object" && content !== null) {
    // Convert object content to formatted markdown for TipTap import
    let markdown = "";

    for (const [key, value] of Object.entries(content)) {
      // Add section header
      const title = key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase());
      markdown += `## ${title}\n\n`;

      // Add content based on type
      if (typeof value === "string") {
        markdown += `${value}\n\n`;
      } else if (Array.isArray(value)) {
        value.forEach((item) => {
          markdown += `- ${typeof item === "string" ? item : JSON.stringify(item)}\n`;
        });
        markdown += "\n";
      } else if (typeof value === "object" && value !== null) {
        // Handle nested objects
        for (const [nestedKey, nestedValue] of Object.entries(value)) {
          markdown += `**${nestedKey}**: ${typeof nestedValue === "string" ? nestedValue : JSON.stringify(nestedValue)}\n\n`;
        }
      } else {
        markdown += `${JSON.stringify(value)}\n\n`;
      }
    }

    return markdown.trim();
  }

  return String(content);
};

/**
 * Simple markdown to HTML converter for fallback
 */
const convertMarkdownToHtml = (markdown: string): string => {
  // First, handle escaped newlines and normalize
  const normalized = markdown
    .replace(/\\n/g, "\n") // Convert escaped newlines to actual newlines
    .replace(/\r\n/g, "\n") // Normalize line endings
    .trim();

  // Split into blocks by double newlines (paragraph breaks)
  const blocks = normalized.split(/\n\s*\n/);

  // Process each block
  const processedBlocks = blocks.map((block) => {
    const trimmedBlock = block.trim();

    // Check if this block is a header
    if (trimmedBlock.match(/^#{1,3}\s/)) {
      return trimmedBlock
        .replace(/^### (.+)$/gm, "<h3>$1</h3>")
        .replace(/^## (.+)$/gm, "<h2>$1</h2>")
        .replace(/^# (.+)$/gm, "<h1>$1</h1>");
    }

    // Check if this block is a list
    if (trimmedBlock.match(/^[\*\-]\s/m)) {
      const listItems = trimmedBlock
        .split("\n")
        .filter((line) => line.trim())
        .map((line) => line.replace(/^[\*\-]\s(.*)$/, "<li>$1</li>"))
        .join("\n");
      return `<ul>\n${listItems}\n</ul>`;
    }

    // Check if this block is a code block
    if (trimmedBlock.startsWith("```") && trimmedBlock.endsWith("```")) {
      const code = trimmedBlock.slice(3, -3).trim();
      return `<pre><code>${code}</code></pre>`;
    }

    // Otherwise, it's a paragraph
    // Process inline formatting within the paragraph
    let processed = trimmedBlock
      // Bold and italic
      .replace(/\*\*\*(.*?)\*\*\*/g, "<strong><em>$1</em></strong>")
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.*?)\*/g, "<em>$1</em>")
      // Inline code
      .replace(/`([^`]+)`/g, "<code>$1</code>");

    // For paragraphs, we can preserve single line breaks as <br>
    // but only within the paragraph, not at the boundaries
    processed = processed.replace(/\n/g, "<br>");

    return `<p>${processed}</p>`;
  });

  // Join all blocks together
  return processedBlocks.join("\n");
};

/**
 * Import markdown content into TipTap editor using native conversion
 */
const importMarkdownToEditor = (
  editor: Editor,
  markdownContent: string,
): Promise<void> => {
  console.log("🔄 TipTap Import - Starting import process...");
  console.log(
    "📄 Content to import:",
    markdownContent.substring(0, 100) + "...",
  );

  // Clean escaped newlines before importing
  const cleanedContent = markdownContent
    .replace(/\\n/g, "\n") // Convert escaped newlines to actual newlines
    .replace(/(#{1,6}\s[^\n]+)\n(?!\n)/g, "$1\n\n") // Ensure double newline after headers
    .replace(/\n{3,}(#{1,6}\s)/g, "\n\n$1") // Remove excess newlines before headers
    .replace(/\n{4,}/g, "\n\n\n") // Clean up multiple consecutive line breaks
    .trim();

  // Normalize markdown tables missing the GFM separator row (| --- | --- |)
  const fixMarkdownTables = (md: string): string => {
    const lines = md.split("\n");
    const out: string[] = [];
    let inTableBlock = false;
    let insertedSeparatorForThisBlock = false;

    const isRow = (s: string) => /^\|.*\|$/.test(s.trim());
    const isSeparator = (s: string) =>
      /^\|?\s*:?-+(\s*\|\s*:?-+)*\s*\|?$/.test(s.trim());

    for (let i = 0; i < lines.length; i++) {
      const current = lines[i];
      const currentIsRow = isRow(current);

      if (currentIsRow) {
        // Entering a new table block
        if (!inTableBlock) {
          inTableBlock = true;
          insertedSeparatorForThisBlock = false;
        }

        out.push(current);

        const next = lines[i + 1] ?? "";
        const nextIsRow = isRow(next);
        const nextIsSeparator = isSeparator(next);

        // Insert exactly one separator after the header row in a contiguous block
        if (!insertedSeparatorForThisBlock && nextIsRow && !nextIsSeparator) {
          const cols = current
            .replace(/\\\|/g, "__P__")
            .split("|")
            .map((c) => c.replace(/__P__/g, "|").trim())
            .filter((c) => c.length > 0).length;
          if (cols > 0) {
            const sep = `| ${Array(cols).fill("---").join(" | ")} |`;
            out.push(sep);
            insertedSeparatorForThisBlock = true;
          }
        }
      } else {
        // Leaving any current table block
        inTableBlock = false;
        insertedSeparatorForThisBlock = false;
        out.push(current);
      }
    }

    return out.join("\n");
  };

  const withFixedTables = fixMarkdownTables(cleanedContent);

  return new Promise((resolve, reject) => {
    try {
      const anyEditor: any = editor as any;
      const proImportCmd = anyEditor?.commands?.import;
      // Create a File object for TipTap Pro Import (expects a File upload)
      const markdownFile = new File([withFixedTables], "content.md", {
        type: "text/markdown",
        lastModified: Date.now(),
      });

      if (typeof proImportCmd === "function") {
        console.log("🔄 Using TipTap Pro Import (editor.commands.import)");
        const invoked: boolean = proImportCmd({
          file: markdownFile,
          // Treat as GitHub Flavored Markdown to preserve lists/headings better
          format: "gfm",
          onImport: (context: any) => {
            if (context?.error) {
              console.warn(
                "⚠️ TipTap Pro import error, falling back to custom converter:",
                context.error,
              );
              // Fallback gracefully to the custom markdown converter
              const htmlContent = convertMarkdownToHtml(cleanedContent);
              if (editor && !editor.isDestroyed) {
                editor.commands.setContent(htmlContent);
                console.log("✅ Fallback markdown conversion successful!");
              }
              return;
            }
            // Ensure content is applied when overriding default handler
            if (context?.setEditorContent && context?.content) {
              context.setEditorContent(context.content);
            }
          },
        });
        if (invoked) {
          console.log("✅ TipTap Pro import invoked successfully!");
          resolve();
          return;
        }
      }

      // Fallback: custom markdown → HTML converter
      console.log("🔄 Using custom markdown converter");
      const htmlContent = convertMarkdownToHtml(withFixedTables);
      if (editor && !editor.isDestroyed) {
        editor.commands.setContent(htmlContent);
        console.log("✅ Custom markdown conversion successful!");
        resolve();
      } else {
        reject(new Error("Editor is not available"));
      }
    } catch (error) {
      console.error("❌ Failed to import markdown:", error);
      reject(error);
    }
  });
};

/**
 * Simple HTML to markdown converter for fallback
 */
const convertHtmlToMarkdown = (html: string): string => {
  const markdown = html
    // Headers
    .replace(/<h1>(.*?)<\/h1>/g, "# $1")
    .replace(/<h2>(.*?)<\/h2>/g, "## $1")
    .replace(/<h3>(.*?)<\/h3>/g, "### $1")
    .replace(/<h4>(.*?)<\/h4>/g, "#### $1")
    .replace(/<h5>(.*?)<\/h5>/g, "##### $1")
    .replace(/<h6>(.*?)<\/h6>/g, "###### $1")
    // Bold and italic
    .replace(/<strong><em>(.*?)<\/em><\/strong>/g, "***$1***")
    .replace(/<em><strong>(.*?)<\/strong><\/em>/g, "***$1***")
    .replace(/<strong>(.*?)<\/strong>/g, "**$1**")
    .replace(/<em>(.*?)<\/em>/g, "*$1*")
    .replace(/<b>(.*?)<\/b>/g, "**$1**")
    .replace(/<i>(.*?)<\/i>/g, "*$1*")
    // Code
    .replace(/<pre><code>(.*?)<\/code><\/pre>/gs, "```\n$1\n```")
    .replace(/<code>(.*?)<\/code>/g, "`$1`")
    // Lists
    .replace(/<ul>(.*?)<\/ul>/gs, (match, content) => {
      return content.replace(/<li>(.*?)<\/li>/g, "- $1\n").trim();
    })
    .replace(/<ol>(.*?)<\/ol>/gs, (match, content) => {
      let counter = 1;
      return content
        .replace(/<li>(.*?)<\/li>/g, () => `${counter++}. $1\n`)
        .trim();
    })
    // Line breaks and paragraphs
    .replace(/<p>(.*?)<\/p>/g, "$1\n\n")
    .replace(/<br\s*\/?>/g, "\n")
    // Remove remaining HTML tags
    .replace(/<[^>]*>/g, "")
    // Clean up extra whitespace
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return markdown;
};

/**
 * Export TipTap content to markdown using native conversion
 */
const exportEditorToMarkdown = (editor: Editor): Promise<string> => {
  console.log("📤 TipTap Export - Starting export process...");

  return new Promise((resolve, reject) => {
    try {
      // Try TipTap Pro Export API
      const anyEditor: any = editor as any;
      const proExportCmd = anyEditor?.commands?.export;
      if (typeof proExportCmd === "function") {
        console.log("🔄 Using TipTap Pro Export (editor.commands.export)");
        const invoked: boolean = proExportCmd({
          format: "md",
          onExport: async (context: any) => {
            if (context?.error) {
              console.error("❌ TipTap Pro export error:", context.error);
              reject(context.error);
              return;
            }
            try {
              const text = await context.blob.text();
              console.log("✅ TipTap export successful!");
              console.log(
                "📝 Exported content preview:",
                text.substring(0, 100) + "...",
              );
              resolve(text);
            } catch (e) {
              reject(e);
            }
          },
        });
        if (invoked) return;
      }

      // Fallback: Convert HTML back to markdown
      console.log(
        "⚠️ TipTap Pro export not available, using fallback converter",
      );
      const htmlContent = editor.getHTML();
      const markdownContent = convertHtmlToMarkdown(htmlContent);
      console.log("✅ Fallback markdown export successful!");
      console.log(
        "📝 Exported content preview:",
        markdownContent.substring(0, 100) + "...",
      );
      resolve(markdownContent);
    } catch (error) {
      console.error("❌ Failed to export markdown:", error);
      reject(error);
    }
  });
};

/**
 * Bubble Menu Component with proper state management
 */
function BubbleMenuComponent({ editor }: { editor: Editor }) {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // Link functionality
  const { url, setUrl, setLink, removeLink } = useLinkPopover({ editor });
  const [showLinkInput, setShowLinkInput] = useState(false);

  // Heading functionality
  const h1 = useHeading({ editor, level: 1 });
  const h2 = useHeading({ editor, level: 2 });
  const h3 = useHeading({ editor, level: 3 });

  useEffect(() => {
    if (!editor) return;

    const updateBubbleMenu = () => {
      const { selection } = editor.state;
      const { from, to } = selection;

      // Only show if there's a text selection
      if (from === to) {
        setIsVisible(false);
        setShowLinkInput(false); // Close link input when no selection
        return;
      }

      try {
        const start = editor.view.coordsAtPos(from);
        const end = editor.view.coordsAtPos(to);

        const editorElement = editor.view.dom;
        const editorRect = editorElement.getBoundingClientRect();

        // Calculate position relative to the editor
        const left = Math.min(start.left, end.left) - editorRect.left;
        const top = start.top - editorRect.top - 50; // 50px above selection

        setPosition({ top, left });
        setIsVisible(true);
      } catch (error) {
        console.error("Error updating bubble menu:", error);
        setIsVisible(false);
        setShowLinkInput(false); // Close link input on error
      }
    };

    const handleBlur = () => {
      // Small delay to allow button clicks
      setTimeout(() => {
        setIsVisible(false);
        setShowLinkInput(false); // Close link input when menu hides
      }, 100);
    };

    // Listen to editor events
    editor.on("selectionUpdate", updateBubbleMenu);
    editor.on("focus", updateBubbleMenu);
    editor.on("blur", handleBlur);

    // Cleanup event listeners with null checks
    return () => {
      if (editor && !editor.isDestroyed) {
        editor.off("selectionUpdate", updateBubbleMenu);
        editor.off("focus", updateBubbleMenu);
        editor.off("blur", handleBlur);
      }
    };
  }, [editor]);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className="absolute z-[9999] bg-gray-900 text-white rounded-lg shadow-lg p-1 flex items-center gap-1"
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      {/* Text Formatting */}
      <Button
        variant="ghost"
        size="sm"
        onMouseDown={(e) => e.preventDefault()} // Prevent focus loss
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setTimeout(() => {
            editor.chain().focus().toggleBold().run();
          }, 10);
        }}
        className={`h-8 w-8 p-0 text-white hover:bg-gray-700 ${
          editor.isActive("bold") ? "bg-gray-700" : ""
        }`}
      >
        <Bold className="h-4 w-4" />
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setTimeout(() => {
            editor.chain().focus().toggleItalic().run();
          }, 10);
        }}
        className={`h-8 w-8 p-0 text-white hover:bg-gray-700 ${
          editor.isActive("italic") ? "bg-gray-700" : ""
        }`}
      >
        <Italic className="h-4 w-4" />
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setTimeout(() => {
            editor.chain().focus().toggleUnderline().run();
          }, 10);
        }}
        className={`h-8 w-8 p-0 text-white hover:bg-gray-700 ${
          editor.isActive("underline") ? "bg-gray-700" : ""
        }`}
      >
        <UnderlineIcon className="h-4 w-4" />
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setTimeout(() => {
            editor.chain().focus().toggleStrike().run();
          }, 10);
        }}
        className={`h-8 w-8 p-0 text-white hover:bg-gray-700 ${
          editor.isActive("strike") ? "bg-gray-700" : ""
        }`}
      >
        <Strikethrough className="h-4 w-4" />
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setTimeout(() => {
            editor.chain().focus().toggleHighlight().run();
          }, 10);
        }}
        className={`h-8 w-8 p-0 text-white hover:bg-gray-700 ${
          editor.isActive("highlight") ? "bg-gray-700" : ""
        }`}
      >
        <Highlighter className="h-4 w-4" />
      </Button>

      {/* Separator */}
      <div className="w-px h-6 bg-gray-600 mx-1" />

      {/* Headings */}
      <Button
        variant="ghost"
        size="sm"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setTimeout(() => {
            h1.toggle();
          }, 10);
        }}
        className={`h-8 w-8 p-0 text-white hover:bg-gray-700 ${
          h1.isActive ? "bg-gray-700" : ""
        }`}
        disabled={!h1.canToggle}
        title="Heading 1"
      >
        <Heading1 className="h-4 w-4" />
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setTimeout(() => {
            h2.toggle();
          }, 10);
        }}
        className={`h-8 w-8 p-0 text-white hover:bg-gray-700 ${
          h2.isActive ? "bg-gray-700" : ""
        }`}
        disabled={!h2.canToggle}
        title="Heading 2"
      >
        <Heading2 className="h-4 w-4" />
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setTimeout(() => {
            h3.toggle();
          }, 10);
        }}
        className={`h-8 w-8 p-0 text-white hover:bg-gray-700 ${
          h3.isActive ? "bg-gray-700" : ""
        }`}
        disabled={!h3.canToggle}
        title="Heading 3"
      >
        <Heading3 className="h-4 w-4" />
      </Button>

      {/* Separator */}
      <div className="w-px h-6 bg-gray-600 mx-1" />

      {/* Link */}
      <Button
        variant="ghost"
        size="sm"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (editor.isActive("link")) {
            setTimeout(() => {
              removeLink();
            }, 10);
          } else {
            setShowLinkInput(!showLinkInput);
          }
        }}
        className={`h-8 w-8 p-0 text-white hover:bg-gray-700 ${
          editor.isActive("link") ? "bg-gray-700" : ""
        }`}
        title={editor.isActive("link") ? "Remove link" : "Add link"}
      >
        <LinkIcon className="h-4 w-4" />
      </Button>

      {/* Link Input (appears when adding link) */}
      {showLinkInput && (
        <div className="flex items-center gap-2 ml-2">
          <input
            type="url"
            value={url || ""}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                setLink();
                setShowLinkInput(false);
              } else if (e.key === "Escape") {
                setShowLinkInput(false);
              }
            }}
            placeholder="Enter URL..."
            className="px-2 py-1 text-sm bg-gray-800 border border-gray-600 rounded text-white placeholder-gray-400 w-48"
            autoFocus
          />
          <Button
            variant="ghost"
            size="sm"
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setLink();
              setShowLinkInput(false);
            }}
            className="h-8 w-8 p-0 text-white hover:bg-gray-700"
            title="Apply link"
          >
            <Check className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Simplified rich text editor for inline editing without external dependencies
 */
function SimpleRichTextEditor({
  content,
  initialJson,
  onSave,
  onCancel,
  isSaving,
  fullPage = false,
}: {
  content?: string;
  initialJson?: JSONContent;
  onSave: (content: any) => void | Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
  fullPage?: boolean;
}) {
  // Debug: Log TipTap environment variables (remove in production)
  console.log(
    "🔧 TipTap Config - AppID:",
    import.meta.env.VITE_TIPTAP_CONVERT_APP_ID ? "✅ Available" : "❌ Missing",
  );
  console.log(
    "🔧 TipTap Config - JWT:",
    import.meta.env.VITE_TIPTAP_CONVERT_JWT ? "✅ Available" : "❌ Missing",
  );

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
        history: {
          depth: 100,
          newGroupDelay: 1000,
        },
        // Disable extensions we're adding separately to avoid duplicates
        link: false,
      }),
      Placeholder.configure({
        placeholder: "Start editing...",
      }),
      Typography,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Subscript,
      Superscript,
      Underline,
      Color,
      TextStyle,
      TaskList,
      TaskItem.configure({ nested: true }),
      Highlight.configure({ multicolor: true }),
      Image,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-blue-600 hover:underline cursor-pointer",
        },
      }),
      // TipTap Pro conversion extensions (token/appId can be refreshed dynamically below)
      Import.configure({
        appId: import.meta.env.VITE_TIPTAP_CONVERT_APP_ID,
        token: import.meta.env.VITE_TIPTAP_CONVERT_JWT,
      }),
      Export.configure({
        appId: import.meta.env.VITE_TIPTAP_CONVERT_APP_ID,
        token: import.meta.env.VITE_TIPTAP_CONVERT_JWT,
      }),
      // Table extensions with proper configuration to avoid ID conflicts
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: "table-auto border-collapse border border-gray-300",
        },
        // Disable allowTableNodeSelection to prevent ID conflicts
        allowTableNodeSelection: false,
      }),
      TableRow.configure({
        HTMLAttributes: {
          class: "border border-gray-300",
        },
      }),
      TableHeader.configure({
        HTMLAttributes: {
          class: "border border-gray-300 bg-gray-50 font-semibold p-2",
        },
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: "border border-gray-300 p-2",
        },
      }),
    ],
    content: initialJson as any,
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none focus:outline-none min-h-[150px] p-6 border rounded-lg",
      },
    },
    immediatelyRender: false,
  });

  // Track last saved content to avoid redundant saves
  const [lastSavedJsonSig, setLastSavedJsonSig] = React.useState<string>(
    initialJson ? JSON.stringify(initialJson) : JSON.stringify({}),
  );

  const { setDirty, forceSave } = useAutoSave({
    onSave: async () => {
      console.log("💾 Auto-save triggered - editor exists:", !!editor);
      if (!editor) {
        console.log("❌ Auto-save aborted - no editor");
        return false;
      }
      if (!onSave) {
        console.log("❌ Auto-save aborted - no onSave callback");
        return false;
      }
      try {
        const json = editor.getJSON();
        const sig = JSON.stringify(json);
        console.log("📝 Saving TipTap JSON:", json);
        console.log("🔄 Calling component onSave prop...");
        await onSave(json);
        setLastSavedJsonSig(sig);
        console.log("✅ Save successful");
        return true;
      } catch (e) {
        console.log("❌ JSON save failed, trying HTML:", e);
        try {
          const html = editor.getHTML();
          console.log("🔄 Calling component onSave prop with HTML...");
          await onSave({ type: "html", html });
          setLastSavedJsonSig(JSON.stringify({ type: "html" }));
          console.log("✅ HTML save successful");
          return true;
        } catch (htmlError) {
          console.log("❌ HTML save also failed:", htmlError);
          return false;
        }
      }
    },
    debounceMs: 1500,
    successMessage: "Changes saved",
  });

  // Mark dirty on editor updates to trigger debounced autosave
  useEffect(() => {
    if (!editor) return;
    const onUpdate = () => {
      console.log("📝 Editor update detected - marking dirty");
      setDirty();
    };
    const onBlur = () => {
      console.log("👋 Editor blur detected - forcing save");
      // Ensure we don't lose edits when switching outputs/tabs
      setTimeout(() => {
        void forceSave();
      }, 0);
    };
    editor.on("update", onUpdate);
    editor.on("blur", onBlur);
    return () => {
      if (editor && !editor.isDestroyed) {
        editor.off("update", onUpdate);
        editor.off("blur", onBlur);
      }
    };
  }, [editor, setDirty, forceSave]);

  // Ensure a final save attempt on unmount
  useEffect(() => {
    return () => {
      void forceSave();
    };
  }, [forceSave]);

  React.useEffect(() => {
    const ensureConvertToken = async () => {
      const tryUrls = [
        "/.netlify/functions/tiptap-token",
        "http://localhost:8888/.netlify/functions/tiptap-token",
      ];
      for (const url of tryUrls) {
        try {
          const res = await fetch(url, { credentials: "omit" });
          if (!res.ok) {
            console.warn(
              `⚠️ TipTap token fetch failed from ${url}: ${res.status}`,
            );
            continue;
          }
          const data = await res.json();
          const token = data?.token as string | undefined;
          const appId = data?.appId as string | undefined;
          if (token && appId && editor && !editor.isDestroyed) {
            const importExt: any = (
              editor as any
            ).extensionManager.extensions.find((e: any) => e.name === "import");
            const exportExt: any = (
              editor as any
            ).extensionManager.extensions.find((e: any) => e.name === "export");
            if (importExt?.options) {
              importExt.options.token = token;
              importExt.options.appId = appId;
            }
            if (exportExt?.options) {
              exportExt.options.token = token;
              exportExt.options.appId = appId;
            }
            console.log(
              "🔐 TipTap Convert token refreshed from server endpoint",
            );
            return; // success
          }
        } catch (e) {
          console.warn(`⚠️ TipTap token fetch threw from ${url}:`, e);
        }
      }
      // Fallback to env token if present
      const envToken = (import.meta as any).env?.VITE_TIPTAP_CONVERT_JWT as
        | string
        | undefined;
      const envAppId = (import.meta as any).env?.VITE_TIPTAP_CONVERT_APP_ID as
        | string
        | undefined;
      if (envToken && envAppId && editor && !editor.isDestroyed) {
        const importExt: any = (editor as any).extensionManager.extensions.find(
          (e: any) => e.name === "import",
        );
        const exportExt: any = (editor as any).extensionManager.extensions.find(
          (e: any) => e.name === "export",
        );
        if (importExt?.options) {
          importExt.options.token = envToken;
          importExt.options.appId = envAppId;
        }
        if (exportExt?.options) {
          exportExt.options.token = envToken;
          exportExt.options.appId = envAppId;
        }
        console.log("🔐 TipTap Convert token loaded from VITE env (fallback)");
      } else {
        console.warn(
          "⚠️ No TipTap token available; Pro import will fallback to custom converter",
        );
      }
    };

    if (!editor) return;
    // If JSON doc provided, it's already set as initial content; just ensure token for export/import if needed later
    if (initialJson) {
      ensureConvertToken();
      return;
    }
    if (content) {
      ensureConvertToken().finally(() => {
        console.log("🚀 Editor initialized - importing content");
        importMarkdownToEditor(editor, content).catch((error) => {
          console.error("❌ Import failed, using setContent fallback:", error);
          if (editor && !editor.isDestroyed) {
            editor.commands.setContent(content);
          }
        });
      });
    }
  }, [editor, content, initialJson]);

  const handleSave = useCallback(async () => {
    // Expose a manual save trigger (not used in UI). Force autosave now.
    await forceSave();
  }, [forceSave]);

  if (!editor) {
    return <div>Loading editor...</div>;
  }

  return (
    <div
      className={`inline-editor border rounded-lg bg-white ${
        fullPage ? "fixed inset-0 z-40 overflow-auto p-4 bg-gray-50" : ""
      }`}
    >
      <EditorContext.Provider value={{ editor }}>
        <div
          className={`editor-wrapper relative ${fullPage ? "max-w-4xl mx-auto" : ""}`}
        >
          <div
            id="editor-saved-indicator"
            className="pointer-events-none select-none absolute -top-7 left-0 text-xs text-gray-500 transition-opacity duration-300 opacity-0"
          >
            Saved
          </div>
          {fullPage && (
            <div className="fixed top-3 right-3 z-50">
              <button
                className="px-3 py-1 text-xs rounded bg-white border shadow hover:bg-gray-50"
                onClick={() => {
                  // Close full screen; parent controls this via prop; we just emit event
                  const ev = new CustomEvent("editor-exit-fullscreen");
                  window.dispatchEvent(ev);
                }}
              >
                Exit full screen
              </button>
            </div>
          )}
          <EditorContent editor={editor} />

          {/* Bubble Menu Component */}
          <BubbleMenuComponent editor={editor} />
        </div>
      </EditorContext.Provider>
    </div>
  );
}

/**
 * Editable version of AIContentRenderer that allows inline editing with rich text editor
 */
export const EditableAIContentRenderer: React.FC<
  EditableAIContentRendererProps
> = ({
  content,
  className = "",
  onSave,
  onEnterEdit,
  canEdit = true,
  schema,
  brandColor,
  alwaysEdit = false,
  persistKey,
  initialJson,
}) => {
  const [editingSections, setEditingSections] = useState<
    Record<string, EditableSection>
  >({});
  const [isSaving, setIsSaving] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Allow external close of full-screen via window event
  React.useEffect(() => {
    const handler = () => setIsFullScreen(false);
    window.addEventListener("editor-exit-fullscreen", handler);
    return () => window.removeEventListener("editor-exit-fullscreen", handler);
  }, []);

  // Allow external request to enter full-screen (scoped by persistKey if provided)
  React.useEffect(() => {
    const onEnter = (e: Event) => {
      try {
        const anyEv = e as CustomEvent<{ persistKey?: string }>;
        const targetKey = anyEv.detail?.persistKey;
        if (!targetKey || targetKey === persistKey) {
          setIsFullScreen(true);
        }
      } catch {
        setIsFullScreen(true);
      }
    };
    window.addEventListener(
      "editor-enter-fullscreen",
      onEnter as EventListener,
    );
    return () =>
      window.removeEventListener(
        "editor-enter-fullscreen",
        onEnter as EventListener,
      );
  }, [persistKey]);
  const { toast } = useToast();
  const [editLocked, setEditLocked] = useState<boolean>(() => {
    try {
      if (typeof window !== "undefined" && persistKey) {
        return localStorage.getItem(`editable-lock-${persistKey}`) === "1";
      }
    } catch (_) {}
    return false;
  });

  useEffect(() => {
    if (!persistKey) return;
    try {
      const v = localStorage.getItem(`editable-lock-${persistKey}`);
      setEditLocked(v === "1");
    } catch (_) {}
  }, [persistKey]);

  const startEditing = useCallback(
    (sectionId: string, currentContent: string) => {
      if (!canEdit) return;

      const section: EditableSection = {
        id: sectionId,
        originalContent: currentContent,
        isEditing: true,
      };

      setEditingSections((prev) => ({
        ...prev,
        [sectionId]: section,
      }));

      if (persistKey) {
        try {
          localStorage.setItem(`editable-lock-${persistKey}`, "1");
          setEditLocked(true);
        } catch (_e) {
          // ignore
        }
      }

      // Notify parent that edit mode was entered (to persist in DB)
      try {
        onEnterEdit && onEnterEdit();
      } catch (_) {}
    },
    [canEdit],
  );

  const cancelEditing = useCallback((sectionId: string) => {
    // No-op to preserve persistent edit mode if desired elsewhere.
    setEditingSections((prev) => ({
      ...prev,
      [sectionId]: { ...prev[sectionId], isEditing: true },
    }));
  }, []);

  const saveEditing = useCallback(
    async (sectionId: string, newContent: string | Record<string, any>) => {
      let section = editingSections[sectionId];
      if (!section) {
        // In always-on edit mode (e.g., after refresh with alwaysEdit=true),
        // the section may not have been initialized via startEditing. Initialize it now.
        section = {
          id: sectionId,
          originalContent: "",
          isEditing: true,
        } as any;
        setEditingSections((prev) => ({
          ...prev,
          [sectionId]: section as any,
        }));
      }

      setIsSaving(true);

      try {
        if (onSave) {
          // If caller passed a whole object payload, persist it as-is
          if (typeof newContent === "object" && newContent !== null) {
            await onSave(newContent);
          } else if (typeof content === "string") {
            // Original content was plain text; save string
            await onSave(newContent);
          } else if (sectionId.startsWith("object-")) {
            // Update a single field in an object payload
            const fieldKey = sectionId.replace("object-", "");
            const updatedContent = {
              ...content,
              [fieldKey]: newContent,
            } as any;
            await onSave(updatedContent);
          } else {
            // Fallback
            await onSave(newContent);
          }
        }

        // Keep editing mode active (persistent editing UX)
        setEditingSections((prev) => ({
          ...prev,
          [sectionId]: { ...(prev[sectionId] as any), isEditing: true },
        }));

        // Suppress noisy success toast; autosave indicators are sufficient
      } catch (error) {
        console.error("Error saving content:", error);
        toast({
          title: "Save Failed",
          description:
            "There was an error saving your changes. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsSaving(false);
      }
    },
    [editingSections, content, onSave, cancelEditing, toast],
  );

  const renderEditableContent = () => {
    let processedContent: any =
      content !== undefined && content !== null ? content : initialJson;

    // Try to parse JSON strings that look like objects
    if (typeof content === "string" && content.trim().startsWith("{")) {
      try {
        processedContent = JSON.parse(content);
        console.log(
          "EditableAIContentRenderer: Successfully parsed JSON string to object",
          processedContent,
        );
      } catch (error) {
        console.log(
          "EditableAIContentRenderer: Failed to parse JSON string, treating as regular string",
          error,
        );
        // If parsing fails, treat as regular string
      }
    }

    if (typeof processedContent === "object" && processedContent !== null) {
      // Direct TipTap doc at root
      if ((processedContent as any).type === "doc") {
        const sectionId = "tiptap-doc-root";
        const isEditing =
          alwaysEdit || editLocked || editingSections[sectionId]?.isEditing;
        return (
          <div className="relative group">
            {/* Show Edit button unless alwaysEdit forces persistent mode */}
            {canEdit && !isEditing && !alwaysEdit && (
              <div className="absolute top-2 right-2 opacity-100">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => startEditing(sectionId, "")}
                  className="flex items-center gap-1 text-xs bg-white shadow-sm"
                >
                  <Edit className="h-3 w-3" />
                  Edit
                </Button>
              </div>
            )}
            {isEditing ? (
              <SimpleRichTextEditor
                fullPage={isFullScreen}
                key={sectionId}
                initialJson={processedContent as any}
                onSave={(newContent) => {
                  // Save entire JSON document back
                  saveEditing(sectionId, newContent as any);
                }}
                onCancel={() => cancelEditing(sectionId)}
                isSaving={isSaving}
              />
            ) : (
              <AIContentRenderer
                content={processedContent}
                schema={schema}
                brandColor={brandColor}
              />
            )}
          </div>
        );
      }
      // Handle structured object content
      // Hide top-level output_title to avoid duplicate rendering under the heading
      const entries = Object.entries(processedContent).filter(
        ([k]) => k !== "output_title",
      );

      // Special case: if after filtering output_title, we only have { content: ... }, render content directly
      if (entries.length === 1 && entries[0][0] === "content") {
        const [, contentValue] = entries[0];
        const sectionId = "unwrapped-content";
        const isEditing =
          alwaysEdit || editLocked || editingSections[sectionId]?.isEditing;

        const isTipTapDoc =
          typeof contentValue === "object" &&
          contentValue &&
          (contentValue as any).type === "doc";

        return (
          <div className="relative group">
            {canEdit && !isEditing && !alwaysEdit && (
              <div className="absolute top-2 right-2 opacity-100">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    startEditing(
                      sectionId,
                      isTipTapDoc ? "" : convertContentToMarkdown(contentValue),
                    )
                  }
                  className="flex items-center gap-1 text-xs bg-white shadow-sm"
                >
                  <Edit className="h-3 w-3" />
                  Edit
                </Button>
              </div>
            )}
            {isEditing ? (
              isTipTapDoc ? (
                <SimpleRichTextEditor
                  fullPage={isFullScreen}
                  initialJson={contentValue as any}
                  onSave={(newContent) => {
                    const updatedObject = {
                      ...(processedContent as any),
                      content: newContent,
                    };
                    saveEditing(sectionId, updatedObject as any);
                  }}
                  onCancel={() => cancelEditing(sectionId)}
                  isSaving={isSaving}
                />
              ) : (
                <SimpleRichTextEditor
                  fullPage={isFullScreen}
                  content={convertContentToMarkdown(contentValue)}
                  onSave={(newContent) => {
                    const updatedObject = {
                      ...(processedContent as any),
                      content: newContent,
                    };
                    saveEditing(sectionId, updatedObject as any);
                  }}
                  onCancel={() => cancelEditing(sectionId)}
                  isSaving={isSaving}
                />
              )
            ) : (
              <AIContentRenderer
                content={contentValue}
                schema={schema}
                brandColor={brandColor}
              />
            )}
          </div>
        );
      }

      return entries.map(([key, value]) => {
        const sectionId = `object-${key}`;
        const isEditing =
          alwaysEdit || editLocked || editingSections[sectionId]?.isEditing;

        return (
          <div key={key} className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <button
                type="button"
                className="w-full flex items-center gap-2 font-semibold text-gray-900 capitalize group text-left"
                onClick={(e) => {
                  const content =
                    (e.currentTarget.parentElement
                      ?.nextSibling as HTMLElement) || null;
                  if (content) {
                    content.classList.toggle("hidden");
                    const icon = e.currentTarget.querySelector(
                      "[data-collapsible-icon]",
                    ) as SVGElement | null;
                    if (icon) {
                      const isHidden = content.classList.contains("hidden");
                      icon.style.transform = isHidden
                        ? "rotate(0deg)"
                        : "rotate(90deg)";
                    }
                  }
                }}
              >
                <svg
                  className="h-4 w-4 text-gray-600 transition-transform duration-200"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  data-collapsible-icon
                >
                  <path
                    d="M9 6l6 6-6 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {key
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (str) => str.toUpperCase())}
              </button>

              <div className="flex items-center gap-2">
                {canEdit && !isEditing && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const contentToEdit = convertContentToMarkdown(value);
                      startEditing(sectionId, contentToEdit);
                    }}
                    className="flex items-center gap-1 text-xs opacity-70 hover:opacity-100"
                  >
                    <Edit className="h-3 w-3" />
                    Edit
                  </Button>
                )}

                <div className="opacity-70 hover:opacity-100">
                  <CopyIconButton
                    content={value}
                    title={key
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^./, (str) => str.toUpperCase())}
                    className="h-7 w-7 hover:bg-gray-200 rounded border border-gray-300 bg-white shadow-sm"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200">
              {isEditing ? (
                <SimpleRichTextEditor
                  content={convertContentToMarkdown(value)}
                  onSave={(newContent) => {
                    // Save back into object under same key
                    const fieldKey = sectionId.replace("object-", "");
                    const updatedContent = {
                      ...processedContent,
                      [fieldKey]: newContent,
                    } as any;
                    saveEditing(sectionId, updatedContent);
                  }}
                  onCancel={() => cancelEditing(sectionId)}
                  isSaving={isSaving}
                />
              ) : typeof value === "string" ? (
                <AIContentRenderer
                  content={value}
                  schema={schema}
                  brandColor={brandColor}
                />
              ) : (
                // Render nested objects/arrays with the same rich renderer used during streaming
                <AIContentRenderer
                  content={value}
                  schema={schema}
                  brandColor={brandColor}
                />
              )}
            </div>
          </div>
        );
      });
    }

    if (typeof processedContent === "string") {
      const sectionId = "string-content";
      const isEditing =
        alwaysEdit || editLocked || editingSections[sectionId]?.isEditing;

      return (
        <div className="relative group">
          {canEdit && !isEditing && (
            <div className="absolute top-2 right-2 opacity-100">
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  startEditing(sectionId, convertContentToMarkdown(content))
                }
                className="flex items-center gap-1 text-xs bg-white shadow-sm"
              >
                <Edit className="h-3 w-3" />
                Edit
              </Button>
            </div>
          )}

          {isEditing ? (
            <SimpleRichTextEditor
              fullPage={isFullScreen}
              content={convertContentToMarkdown(processedContent)}
              onSave={(newContent) => {
                const convertedContent = newContent; // TipTap native export returns markdown
                saveEditing(sectionId, convertedContent);
              }}
              onCancel={() => cancelEditing(sectionId)}
              isSaving={isSaving}
            />
          ) : (
            <AIContentRenderer
              content={processedContent}
              schema={schema}
              brandColor={brandColor}
            />
          )}
        </div>
      );
    }

    // Fallback for other types
    return (
      <AIContentRenderer
        content={content}
        schema={schema}
        brandColor={brandColor}
      />
    );
  };

  return (
    <div className={`editable-ai-content-renderer ${className}`}>
      {renderEditableContent()}
    </div>
  );
};
